import polygonClipping from 'polygon-clipping';
import type { FemaRegulatoryAttributes, FemaRegulatoryContext, FemaRegulatoryKind, FemaRegulatoryRecord, FemaRegulatoryScope } from '../../types/femaRegulatoryContext';
import type { RefdataLatLng } from '../../types/refdata';
import { createRawParcelBoundarySnapshot } from '../../lib/generationImpact/boundaryAuthority';
import { isFemaSourceContextFeature } from '../refdata/femaContextContract';
import type { FemaSourceContextAssessment } from './femaSourceContext';
import { boundaryFingerprint, multiBoundaryFingerprint } from './types';

const LAYERS: Record<FemaRegulatoryKind, string> = { 'firm-panel': 'S_FIRM_PAN', 'political-area': 'S_POL_AR', lomr: 'S_LOMR' };
const QUALIFICATION = 'Intersecting FEMA panel, jurisdiction and map-revision records are regulatory context. They do not change flood classification, establish complete mapping, prove absence of map revisions, or grant development approval.';
const JOIN = 'exact-native-product-dfirm-source-citation-join-v1';
function scalarAttributes(value: unknown): FemaRegulatoryAttributes | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value);
  if (entries.some(([, v]) => v !== null && !['string', 'number', 'boolean'].includes(typeof v))) return null;
  return Object.fromEntries(entries) as FemaRegulatoryAttributes;
}
function text(value: unknown): string | undefined { return typeof value === 'string' && value.trim() ? value.trim() : undefined; }
/** FEMA sentinel dates remain in raw attributes, never displayed as effective dates. */
export function femaSourceEffectiveDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z?)?$/.test(value)) return undefined;
  const day = value.slice(0, 10), year = Number(day.slice(0, 4));
  if (year < 1900 || year >= 8888) return undefined;
  const parsed = new Date(day + 'T00:00:00Z');
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : undefined;
}
function citations(props: FemaRegulatoryAttributes): Pick<FemaRegulatoryRecord, 'citations' | 'citationJoinStatus'> {
  if (!props.sourceCitationRecordsJson) return { citations: [], citationJoinStatus: 'not-supplied' };
  try {
    if (props.sourceCitationJoinBasis !== JOIN || typeof props.sourceCitationRecordsJson !== 'string' || props.sourceCitationRecordsJson.length > 1024 * 1024 || !text(props.DFIRM_ID) || !text(props.SOURCE_CIT)) throw new Error('Unqualified citation join');
    const parsed: unknown = JSON.parse(props.sourceCitationRecordsJson);
    if (!Array.isArray(parsed) || !parsed.length || parsed.length > 1000) throw new Error('Invalid citation rows');
    const result: FemaRegulatoryRecord['citations'] = [];
    for (const row of parsed) {
      if (!row || typeof row !== 'object' || row.sourceLayer !== 'L_SOURCE_CIT' || !Number.isSafeInteger(row.sourceFid) || row.sourceFid < 1) throw new Error('Invalid source identity');
      const attributes = scalarAttributes(row.properties);
      if (!attributes || attributes.DFIRM_ID !== props.DFIRM_ID || attributes.SOURCE_CIT !== props.SOURCE_CIT) throw new Error('Citation keys differ');
      result.push({ sourceLayer: 'L_SOURCE_CIT', sourceFid: row.sourceFid, attributes });
    }
    return { citations: result, citationJoinStatus: 'matched' };
  } catch { return { citations: [], citationJoinStatus: 'invalid' }; }
}
function ringContains(ring: number[][], point: RefdataLatLng): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!, b = ring[j]!;
    if ((a[1]! > point.lat) !== (b[1]! > point.lat) && point.lng < (b[0]! - a[0]!) * (point.lat - a[1]!) / (b[1]! - a[1]!) + a[0]!) inside = !inside;
  }
  return inside;
}
function hasArea(polygons: polygonClipping.MultiPolygon): boolean {
  return polygons.some(polygon => {
    const areas = polygon.map(ring => Math.abs(ring.reduce((sum, p, i) => {
      const q = ring[(i + 1) % ring.length]!; return sum + p[0] * q[1] - q[0] * p[1];
    }, 0)));
    return (areas[0] ?? 0) - areas.slice(1).reduce((a, b) => a + b, 0) > 0;
  });
}
export function projectFemaRegulatoryContext(
  context: FemaSourceContextAssessment,
  target: {
    point: RefdataLatLng;
    polygon?: readonly RefdataLatLng[];
    polygons?: ReadonlyArray<{
      id?: string;
      polygon: readonly RefdataLatLng[];
      holes?: ReadonlyArray<readonly RefdataLatLng[]>;
    }>;
  },
): FemaRegulatoryContext {
  const isPolygon = Boolean(target.polygon || (target.polygons && target.polygons.length > 0));
  const fp = target.polygons?.length
    ? multiBoundaryFingerprint(target.polygons)
    : target.polygon
      ? boundaryFingerprint(target.polygon)
      : null;
  const scope: FemaRegulatoryScope = {
    queryKind: isPolygon ? 'polygon' : 'point',
    status: context.status,
    reason: context.reason,
    ...(context.binding ? { binding: context.binding } : {}),
    records: [],
    rejectedRecordCount: context.rejectedFeatureCount,
    ...(isPolygon ? { boundaryFingerprint: fp ?? undefined } : { point: target.point }),
  };
  const result: FemaRegulatoryContext = { schemaVersion: 1, status: scope.status, qualification: QUALIFICATION, scopes: [scope] };
  const fail = (reason: string) => { scope.status = result.status = 'unavailable'; scope.reason = reason; return result; };
  if (!context.binding || context.status === 'unavailable') return result;
  let footprint: polygonClipping.MultiPolygon | polygonClipping.Polygon | undefined;
  const closeRing = (pts: readonly RefdataLatLng[]): polygonClipping.Pair[] => {
    const ring: polygonClipping.Pair[] = pts.map(p => [p.lng, p.lat]);
    if (ring[0]![0] !== ring[ring.length - 1]![0] || ring[0]![1] !== ring[ring.length - 1]![1]) ring.push(ring[0]!);
    return ring;
  };
  try {
    if (target.polygons && target.polygons.length > 0) {
      createRawParcelBoundarySnapshot({
        siteBoundaries: target.polygons.map((p, idx) => ({
          id: p.id ?? `p${idx + 1}`,
          polygon: [...p.polygon],
          ...(p.holes?.length ? { holes: p.holes.map(h => [...h]) } : {}),
        })) as unknown as import('../../types/siteBoundary').SiteBoundaryParcel[],
      });
      footprint = target.polygons.map(p => [
        closeRing(p.polygon),
        ...(p.holes ?? []).map(closeRing),
      ]) as polygonClipping.MultiPolygon;
    } else if (target.polygon) {
      createRawParcelBoundarySnapshot({ siteBoundary: [...target.polygon] });
      footprint = [closeRing(target.polygon)];
    } else if (!Number.isFinite(target.point.lat) || !Number.isFinite(target.point.lng) || Math.abs(target.point.lat) > 90 || Math.abs(target.point.lng) > 180) throw new Error('Invalid point');
  } catch { return fail('The selected study footprint is invalid; regulatory context cannot be spatially assigned.'); }
  const seen = new Map<string, FemaRegulatoryRecord>();
  const conflicting = new Set<string>();
  for (const kind of Object.keys(LAYERS) as FemaRegulatoryKind[]) for (const feature of context.byKind[kind]) {
    const props = feature.props;
    if (!isFemaSourceContextFeature(feature, context.binding) || props.SOURCE_ROLE !== 'official-native' || props.sourceLayer !== LAYERS[kind] || !text(props.sourceProductId)
      || !Number.isSafeInteger(props.sourceFid) || Number(props.sourceFid) < 1 || props.SOURCE_VERSION !== props.sourceProductId
      || typeof props.SOURCE_SHA256 !== 'string' || !/^[a-f0-9]{64}$/.test(props.SOURCE_SHA256)
      || !props.sourceFeatureId.startsWith(`${props.sourceProductId}:${props.sourceLayer}:${props.sourceFid}:`) || props.STATUS === 'Source_Unavailable' || (props.nativeValid as unknown) === false) { scope.rejectedRecordCount++; continue; }
    try {
      const polygons = feature.geom.type === 'Polygon' ? [feature.geom.coordinates] : feature.geom.type === 'MultiPolygon' ? feature.geom.coordinates : [];
      const intersects = footprint ? hasArea(polygonClipping.intersection(footprint, polygons as polygonClipping.MultiPolygon))
        : polygons.some(polygon => ringContains(polygon[0]!, target.point) && !polygon.slice(1).some(ring => ringContains(ring, target.point)));
      if (!intersects) continue;
      const { sourceCitationRecordsJson: _rawJoin, ...attributes } = props;
      const citation = citations(props);
      if (citation.citationJoinStatus === 'invalid') scope.rejectedRecordCount++;
      const effectiveDate = femaSourceEffectiveDate(kind === 'firm-panel' ? props.EFF_DATE : kind === 'lomr' ? props.EFF_DATE : undefined);
      const record: FemaRegulatoryRecord = { kind, sourceFeatureId: props.sourceFeatureId, sourceProductId: String(props.sourceProductId), sourceLayer: String(props.sourceLayer), sourceFid: Number(props.sourceFid), sourceReceiptSha256: props.sourceReceiptSha256,
        attributes, ...citation, ...(effectiveDate ? { effectiveDate, effectiveDateField: 'EFF_DATE' } : {}) };
      const prior = seen.get(record.sourceFeatureId);
      if (conflicting.has(record.sourceFeatureId)) continue;
      if (prior && JSON.stringify(prior) !== JSON.stringify(record)) { scope.rejectedRecordCount++; seen.delete(record.sourceFeatureId); conflicting.add(record.sourceFeatureId); continue; }
      seen.set(record.sourceFeatureId, record);
    } catch { scope.rejectedRecordCount++; }
  }
  scope.records = [...seen.values()].sort((a, b) => a.sourceFeatureId.localeCompare(b.sourceFeatureId));
  if (scope.rejectedRecordCount > 0) { scope.status = result.status = 'partial'; scope.reason = 'Valid intersecting context is retained; unavailable source geometry, invalid joins or rejected rows leave regulatory completeness unresolved.'; }
  return result;
}
export function aggregateFemaRegulatoryContext(items: Array<{ parcelId: string; context?: FemaRegulatoryContext }>): FemaRegulatoryContext | undefined {
  if (!items.some(item => item.context)) return undefined;
  const scopes: FemaRegulatoryScope[] = items.flatMap(item => item.context ? item.context.scopes.map(scope => ({ ...scope, parcelId: item.parcelId })) : [{ parcelId: item.parcelId, queryKind: 'polygon' as const, status: 'unavailable' as const, reason: 'This parcel has no qualified regulatory context query.', records: [], rejectedRecordCount: 0 }]);
  return { schemaVersion: 1, status: scopes.every(scope => scope.status === 'verified') ? 'verified' : scopes.every(scope => scope.status === 'unavailable') ? 'unavailable' : 'partial', qualification: QUALIFICATION, scopes };
}
